import httpService from "..";

export const getReportsApi = async () => {
  return await httpService(
    `${process.env.REACT_APP_API_URL}/api/${process.env.REACT_APP_VERSION}/report`,
    "get"
  );
};

/** One bar in Sales by Group (BUG-21 / BUG-24). */
export type SalesByGroupRow = {
  groupId: number | string;
  groupName: string;
  policyCount: number;
  salesAmount: number;
  /** Present after BUG-24: GroupLevel of this bucket. */
  groupLevel?: number;
  /** Present after BUG-24: parent group id when not the tree root. */
  parentGroupId?: number | string | null;
};

/**
 * Dashboard aggregates.
 * When `groupId` is set, FE always requests `includeSubgroups=true` (BUG-24):
 * BE should return `salesByGroup` for the requested group + all descendants.
 * Older BE builds that ignore the flag still work (Level-1 rollup only).
 */
export const getDashboardReportApi = async (
  period: string,
  groupId?: string
) => {
  const qs = new URLSearchParams({ period });
  if (groupId) {
    qs.set("groupId", String(groupId));
    qs.set("includeSubgroups", "true");
  }
  return await httpService(
    `${process.env.REACT_APP_API_URL}/api/${process.env.REACT_APP_VERSION}/report/dashboard?${qs.toString()}`,
    "get"
  );
};

export type DashboardDrilldownChart =
  | "age"
  | "cover"
  | "day"
  | "collections"
  | "group";

export type DashboardDrilldownParams = {
  period: string;
  groupId?: string | number;
  chart: DashboardDrilldownChart;
  key: string;
};

export type DashboardDrilldownRow = {
  id?: string | number;
  MemberID?: string | number;
  IdNumber?: string;
  PolicyNumber?: string;
  Name?: string;
  Surname?: string;
  GroupName?: string;
  ProductName?: string;
  DateRegistered?: string;
  Premium?: number | string;
  CoverAmount?: number | string;
  ViewPath?: string;
};

/**
 * Member/policy rows for a clicked dashboard chart bucket.
 * For `chart=group`, `key` is the sales bucket `groupId` (owning L1 today;
 * after BUG-24, any group in the filtered tree). When scoped by `groupId`,
 * FE also sends `includeSubgroups=true` so BE resolves `key` as that
 * group's own members (not re-rolled to owning Level 1).
 */
export const getDashboardDrilldownApi = async (
  params: DashboardDrilldownParams
) => {
  const qs = new URLSearchParams({
    period: params.period,
    chart: params.chart,
    key: params.key,
  });
  if (params.groupId != null && params.groupId !== "") {
    qs.set("groupId", String(params.groupId));
    if (params.chart === "group") {
      qs.set("includeSubgroups", "true");
    }
  }
  return await httpService(
    `${process.env.REACT_APP_API_URL}/api/${process.env.REACT_APP_VERSION}/report/dashboard/drilldown?${qs.toString()}`,
    "get"
  );
};

export const addPaymentReportApi = async (
  data: {},
  groupLinkId: any,
  groupLevel: any,
  groupid: any
) => {
  return await httpService(
    `${process.env.REACT_APP_API_URL}/api/${process.env.REACT_APP_VERSION}/report/payment/${groupLinkId}?groupLevel=${groupLevel}&groupid=${groupid}`,
    "post",
    data
  );
};

export const addActivePolicyReportApi = async (
  data: {},
  groupLinkId: any,
  groupLevel: any,
  groupid: any
) => {
  return await httpService(
    `${process.env.REACT_APP_API_URL}/api/${process.env.REACT_APP_VERSION}/report/active-policies/${groupLinkId}?groupLevel=${groupLevel}&groupid=${groupid}`,
    "post",
    data
  );
};

export const addNonPaymentReportApi = async (
  data: {},
  groupLinkId: any,
  groupLevel: any,
  groupid: any
) => {
  return await httpService(
    `${process.env.REACT_APP_API_URL}/api/${process.env.REACT_APP_VERSION}/report/non-payment/${groupLinkId}?groupLevel=${groupLevel}&groupid=${groupid}`,
    "post",
    data
  );
};

export const getPowerBIEmbedConfigApi = async () => {
  return await httpService(
    `${process.env.REACT_APP_API_URL}/api/${process.env.REACT_APP_VERSION}/report/powerbi/embed-config`,
    "get"
  );
};

export const getReportAccessConfigApi = async (groupId: number) => {
  return await httpService(
    `${process.env.REACT_APP_API_URL}/api/${process.env.REACT_APP_VERSION}/report/access-config/${groupId}`,
    "get"
  );
};

export const updateReportAccessConfigApi = async (
  groupId: number,
  enabledReports: string[]
) => {
  return await httpService(
    `${process.env.REACT_APP_API_URL}/api/${process.env.REACT_APP_VERSION}/report/access-config/${groupId}`,
    "put",
    { config: { enabledReports } }
  );
};
